'use client';

import * as React from 'react';
import { Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { formatTime } from './playerTime';

const BAR_COUNT = 48;
const SPEEDS = [1, 1.5, 2, 0.5] as const;

/**
 * Deterministic per-bar heights, seeded by meeting id so the same recording
 * always renders the same shape. There's no real amplitude data here — that
 * would mean decoding the whole audio file client-side just to draw a
 * picture — so this is decorative; the played/unplayed split (and every
 * control) is wired to the real `<audio>` element's actual state.
 */
function barHeights(seed: string): number[] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const heights: number[] = [];
  for (let i = 0; i < BAR_COUNT; i++) {
    h = (h * 1103515245 + 12345) >>> 0;
    heights.push(0.28 + ((h >>> 8) % 1000) / 1000 / 1.4);
  }
  return heights;
}

export function AudioWaveformPlayer({
  src,
  seed,
  onError,
  ariaLabel,
}: {
  src: string;
  seed: string;
  onError: () => void;
  ariaLabel: string;
}) {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [muted, setMuted] = React.useState(false);
  const [speedIndex, setSpeedIndex] = React.useState(0);
  const heights = React.useMemo(() => barHeights(seed), [seed]);

  const progress = duration > 0 ? currentTime / duration : 0;

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) el.pause();
    else void el.play();
  };

  const seekTo = (fraction: number) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    el.currentTime = Math.min(duration, Math.max(0, fraction * duration));
  };

  const onSeekClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    seekTo((e.clientX - rect.left) / rect.width);
  };

  const onSeekKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    if (e.key === 'ArrowRight') {
      el.currentTime = Math.min(duration, el.currentTime + 5);
    } else if (e.key === 'ArrowLeft') {
      el.currentTime = Math.max(0, el.currentTime - 5);
    } else {
      return;
    }
    e.preventDefault();
  };

  const cycleSpeed = () => {
    const next = (speedIndex + 1) % SPEEDS.length;
    setSpeedIndex(next);
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[next];
  };

  const toggleMute = () => {
    const el = audioRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  return (
    <div className="flex items-center gap-3 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-4 py-3">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- a spoken-call recording has no meaningful captions track to author */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onError={onError}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        aria-label={ariaLabel}
        className="hidden"
      />

      <button
        type="button"
        onClick={togglePlay}
        aria-label={playing ? 'Pause' : 'Play'}
        className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[var(--primary)] text-white transition-transform active:scale-95"
      >
        {playing ? (
          <Pause className="h-4 w-4" fill="currentColor" aria-hidden="true" />
        ) : (
          <Play className="h-4 w-4 translate-x-0.5" fill="currentColor" aria-hidden="true" />
        )}
      </button>

      <div
        role="slider"
        tabIndex={0}
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(currentTime)}
        onClick={onSeekClick}
        onKeyDown={onSeekKeyDown}
        className="flex h-8 flex-1 cursor-pointer items-end gap-[2px] outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
      >
        {heights.map((h, i) => {
          const played = i / BAR_COUNT < progress;
          return (
            <span
              key={i}
              style={{ height: `${h * 100}%` }}
              className={`w-full rounded-full transition-colors ${
                played ? 'bg-[var(--primary)]' : 'bg-[var(--border)]'
              }`}
            />
          );
        })}
      </div>

      <span className="shrink-0 text-xs tabular-nums text-[var(--text-muted)]">
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>

      <button
        type="button"
        onClick={cycleSpeed}
        aria-label="Playback speed"
        className="shrink-0 rounded-full border border-[var(--border)] px-2 py-1 text-xs font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]"
      >
        {SPEEDS[speedIndex]}x
      </button>

      <button
        type="button"
        onClick={toggleMute}
        aria-label={muted ? 'Unmute' : 'Mute'}
        className="shrink-0 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
      >
        {muted ? (
          <VolumeX className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Volume2 className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
